import {
  MSG_KINDS,
  type ViewerMode,
  type ViewerEventName,
  type ViewerEventPayloads,
  type RingClientApi,
  type BandClientApi,
  type RingBuilderClientApi,
  type EngagementApi,
  type Promisify,
  type ViewerToHostMsg,
} from "../shared/api-contract";

/** @deprecated v37 — use `RingBuilderClientApi` (the merged band + engagement
 * facade). Kept because ui/engagement still imports it. */
export type EngagementClientApi = Promisify<EngagementApi>;

interface Pending { resolve: (v: unknown) => void; reject: (e: Error) => void; }

/**
 * A hook that gets to run — and to `await` — before every outgoing RPC.
 *
 * `call()` is the one place every host→viewer request passes through: the typed
 * `.ring` / `.ringBuilder` facades are Proxies over it, and so is any direct
 * `client.call()`. That makes it the only place a rule about the live scene can
 * be enforced without trusting each call site to remember it.
 *
 * The sole user is the configurator drawer's live preview, which has to put the
 * real design back on the model before anything can read the model out (see
 * features/vault/configuratorPreview.ts). Keep it that way: this is a safety
 * interlock, not general-purpose middleware — a guard that blocks or slows the
 * bridge slows every RPC in the app.
 */
export interface RpcGuard {
  /** Rejecting aborts the call, which is the point: a guard that cannot make the
   *  scene safe must not let the request through. */
  beforeCall(method: string, args: readonly unknown[]): Promise<void> | void;
}

/**
 * Host-side half of the postMessage bridge. Wraps an <iframe> running the
 * headless viewer and exposes a fully-typed, Promise-based API plus an
 * EventTarget for viewer-originated events (ready, selectionChanged, ...).
 */
export class ViewerClient extends EventTarget {
  private pending = new Map<number, Pending>();
  private nextId = 1;
  private _ready = false;
  private _mode: ViewerMode | null = null;
  private _contractVersion = 0;
  private readyWaiters: Array<() => void> = [];
  private handshakeTimer: number | null = null;
  private _rpcGuard: RpcGuard | null = null;

  /** Typed RING facade. Every method returns a Promise. */
  readonly ring: RingClientApi;
  /** Typed RING BUILDER facade (v37: band + engagement merged). Every method
   * returns a Promise. Prefer this over `band` / `engagement`. */
  readonly ringBuilder: RingBuilderClientApi;
  /** @deprecated v37 — use `ringBuilder`. */
  readonly band: BandClientApi;
  /** @deprecated v37 — use `ringBuilder`. */
  readonly engagement: EngagementClientApi;

  constructor(
    private iframe: HTMLIFrameElement,
    /** Viewer origin, or "*" to skip origin checks (dev only). */
    private viewerOrigin: string,
  ) {
    super();
    window.addEventListener("message", this._onMessage);
    this.ring = this._makeProxy<RingClientApi>();
    this.ringBuilder = this._makeProxy<RingBuilderClientApi>();
    this.band = this._makeProxy<BandClientApi>();
    this.engagement = this._makeProxy<EngagementClientApi>();
    this._startHandshake();
  }

  get ready() { return this._ready; }
  get mode() { return this._mode; }
  /** Contract version the connected viewer announced (0 until ready). */
  get contractVersion() { return this._contractVersion; }

  /** The installed RPC interlock, if any — see `RpcGuard`. */
  get rpcGuard(): RpcGuard | null { return this._rpcGuard; }
  /** Install the interlock. Only one at a time; the caller owns removing it. */
  setRpcGuard(guard: RpcGuard) { this._rpcGuard = guard; }
  /** Remove `guard` — a no-op if a different one has since been installed, so a
   *  late teardown can never strip the live guard out from under its owner. */
  clearRpcGuard(guard: RpcGuard) { if (this._rpcGuard === guard) this._rpcGuard = null; }

  whenReady(): Promise<void> {
    if (this._ready) return Promise.resolve();
    return new Promise((resolve) => this.readyWaiters.push(resolve));
  }

  /** Listen for a viewer event with a typed payload. */
  on<K extends ViewerEventName>(name: K, handler: (payload: ViewerEventPayloads[K]) => void): () => void {
    const wrapped = (e: Event) => handler((e as CustomEvent).detail as ViewerEventPayloads[K]);
    this.addEventListener(name, wrapped);
    return () => this.removeEventListener(name, wrapped);
  }

  /** Low-level typed RPC call. Most callers should use `.ring` / `.ringBuilder`. */
  async call<T = unknown>(method: string, ...args: unknown[]): Promise<T> {
    await this.whenReady();
    // The interlock runs (and is awaited) before the request is posted, so
    // anything it does to the scene is already applied — and acknowledged by the
    // viewer — by the time this call reaches it. postMessage preserves order.
    if (this._rpcGuard) await this._rpcGuard.beforeCall(method, args);
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this._post({ kind: MSG_KINDS.rpc, id, method, args });
    });
  }

  dispose() {
    this._rpcGuard = null;
    window.removeEventListener("message", this._onMessage);
    if (this.handshakeTimer != null) window.clearInterval(this.handshakeTimer);
    this.pending.clear();
  }

  // ─── internals ────────────────────────────────────────────────────

  private _makeProxy<T>(): T {
    return new Proxy({}, {
      get: (_t, prop: string) => (...args: unknown[]) => this.call(prop, ...args),
    }) as T;
  }

  private _post(msg: unknown) {
    this.iframe.contentWindow?.postMessage(msg, this.viewerOrigin);
  }

  private _startHandshake() {
    // The viewer also auto-announces on load, but it may boot before we attach.
    // Poll a hello until we receive ready.
    // Only once the frame holds the viewer's page: a ping into the initial
    // about:blank document (same origin as the host) makes the browser log
    // "target origin … does not match the recipient window's origin" — seen on a
    // first mobile load, where the interval fires before navigation commits.
    const ping = () => { if (!this._ready && this._navigated()) this._post({ kind: MSG_KINDS.hello }); };
    this.handshakeTimer = window.setInterval(ping, 250);
    // First ping once the iframe has at least navigated.
    this.iframe.addEventListener("load", ping);
  }

  /**
   * True once the iframe's window can be the viewer's: a cross-origin window
   * (its location is unreadable) or a same-origin one already at the viewer's
   * origin. The initial about:blank document is readable and is the host's own
   * origin, so a hello posted to it is refused by the browser with a warning.
   */
  private _navigated(): boolean {
    const w = this.iframe.contentWindow;
    if (!w) return false;
    if (this.viewerOrigin === "*") return true;
    try {
      const href = w.location.href;
      return href !== "about:blank" && w.location.origin === this.viewerOrigin;
    } catch {
      return true;
    }
  }

  private _onMessage = (ev: MessageEvent) => {
    if (ev.source !== this.iframe.contentWindow) return;
    if (this.viewerOrigin !== "*" && ev.origin !== this.viewerOrigin) return;
    const msg = ev.data as ViewerToHostMsg | null;
    if (!msg || typeof msg !== "object") return;

    switch (msg.kind) {
      case MSG_KINDS.ready: {
        if (!this._ready) {
          this._ready = true;
          this._mode = msg.mode;
          this._contractVersion = msg.contractVersion;
          if (this.handshakeTimer != null) { window.clearInterval(this.handshakeTimer); this.handshakeTimer = null; }
          this.readyWaiters.splice(0).forEach((fn) => fn());
          this.dispatchEvent(new CustomEvent("ready", { detail: { mode: msg.mode, contractVersion: msg.contractVersion } }));
        }
        break;
      }
      case MSG_KINDS.result: {
        const p = this.pending.get(msg.id);
        if (p) { this.pending.delete(msg.id); p.resolve(msg.result); }
        break;
      }
      case MSG_KINDS.error: {
        const p = this.pending.get(msg.id);
        if (p) { this.pending.delete(msg.id); p.reject(new Error(msg.error)); }
        break;
      }
      case MSG_KINDS.event: {
        this.dispatchEvent(new CustomEvent(msg.name, { detail: msg.payload }));
        break;
      }
    }
  };
}
